import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';

import RespondentContext from './Context/Respondent';
import Respondent from './Records/Respondent';
import RespondentRecord from './Records/Respondent';
import { getRespondent } from './Api/TestimonialRespondent';
import TestimonialContext from '~/App/Modules/Testimonials/Context/TestimonialContext';

import TestimonialPageContainer from './Containers/TestimonialPageContainer';
import OutroPageContainer from './Containers/OutroPageContainer';
import Loader from '~/App/Components/Shared/Loader';
import Sidebar from '~/App/Modules/Testimonials/Components/Common/Sidebar';
import Error404 from '~/Errors/Pages/error404';
import { defaultTestimonialText } from '~/App/Modules/Testimonials/Pages/Editor/constants';
import { useFeatures } from '~/react-hooks/use-features';
import { getTestimonialLogoUrl } from '~/App/Modules/Testimonials/util';
import { getRichTextFromSlateData } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';

export const landingPageBaseRoute = '/te/:name/:id';

interface LogoProps {
  src?: string;
}

function Logo({ src }: LogoProps) {
  return (
    <div className='absolute top-[7vh] w-full items-center -z-50 hidden md:!flex flex-col'>
      {src && <img src={src} className='w-auto h-auto max-h-[60px]' />}
    </div>
  );
}

export default function ResponsesRoutes() {
  const { user, text, landing_page, metadata } = useContext(TestimonialContext);
  const [respondent, setRespondent] = useState<Respondent>();
  const collapseAtWidthSize = 1024;

  const { text_color, font_family } = metadata?.style ?? {};

  const instructions = useMemo(
    () =>
      getRichTextFromSlateData(
        text.instruction_rich ?? defaultTestimonialText.instruction_rich
      ),
    [text]
  );

  const { shouldHideBonjoroTestimonialBranding } = useFeatures();
  const logoUrl = getTestimonialLogoUrl(
    shouldHideBonjoroTestimonialBranding,
    landing_page.logo
  );

  const { id: testimonial_id, respondent_id } =
    useParams<{ id: string; respondent_id: string }>();

  const {
    data: respondentData,
    isLoading,
    isError,
  } = useQuery(
    ['respondent', [testimonial_id, respondent_id]],
    () => getRespondent(testimonial_id, respondent_id),
    {
      retry: false,
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
    }
  );

  useEffect(() => {
    if (respondentData) {
      const respondentRecord = new RespondentRecord(respondentData);
      setRespondent(respondentRecord);
    }
  }, [respondentData]);

  if (isError) {
    return <Error404 />;
  }

  if (isLoading || !respondent || !respondentData?.questions) {
    return <Loader className='h-full' />;
  }

  return (
    <RespondentContext.Provider
      value={{
        respondent,
        setRespondent,
      }}
    >
      <div className='flex flex-col bg-white md:bg-transparent lg:flex-row-reverse h-full z-10 overflow-x-auto overflow-y-hidden'>
        <Sidebar
          user={user}
          instructions={instructions}
          collapseAtWidthSize={collapseAtWidthSize}
          fontColor={text_color}
          fontFamily={font_family}
        />
        {/* Adjust height based on 4 rem of the Sidebar on mobile*/}
        <div className='h-[calc(100%-4rem)] relative flex flex-col lg:h-full w-full flex-grow'>
          {landing_page.logo?.url && <Logo src={logoUrl ?? undefined} />}
          <BrowserRouter>
            <Switch>
              <Route
                exact
                path={`${landingPageBaseRoute}/:respondent_id/submitted`}
                component={OutroPageContainer}
              />
              <Route component={TestimonialPageContainer} />
            </Switch>
          </BrowserRouter>
        </div>
      </div>
    </RespondentContext.Provider>
  );
}
